import { API_HOST, EXCHANGE_PATH } from './config'
import type { ApiEnvelope, ConnectData, JoinErrorKind, PrejoinData } from './types'
import { fetchWithTimeout } from './support'

export class JoinError extends Error {
  kind: JoinErrorKind
  /**
   * Where the class went, when the server told us. Only ever set for `switched`:
   * a 409 from a class that fell back to Zoom carries the join URL so the error
   * screen can offer a tap-through instead of sending a child to go and look for
   * their lesson. See Meetings::ConnectionDetails#moved_to_zoom.
   */
  movedUrl?: string

  constructor(kind: JoinErrorKind, message: string, movedUrl?: string) {
    super(message)
    this.kind = kind
    this.movedUrl = movedUrl
  }
}

// Congested Cairo mobile networks can leave a request hanging indefinitely;
// without a deadline the join screen just sits there with no way forward.
const REQUEST_TIMEOUT_MS = 15_000

async function post<T>(body: Record<string, string>): Promise<T> {
  let response: Response
  try {
    response = await fetchWithTimeout(
      `${API_HOST}${EXCHANGE_PATH}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The exchange endpoint is unauthenticated (the code IS the credential);
        // never send cookies.
        credentials: 'omit',
        cache: 'no-store',
        body: JSON.stringify(body),
      },
      REQUEST_TIMEOUT_MS,
    )
  } catch {
    throw new JoinError('network', 'Could not reach the server')
  }

  let envelope: ApiEnvelope<T> | undefined
  try {
    envelope = (await response.json()) as ApiEnvelope<T>
  } catch {
    /* non-JSON error body — classified by status below */
  }

  if (!response.ok || envelope?.status !== 'success') {
    throw classify(response.status, envelope?.message, envelope?.data)
  }
  return envelope.data
}

/**
 * The error envelope's `data` is `[]` for almost every failure — only the
 * moved-to-Zoom 409 attaches anything. Read defensively: an array, a null or a
 * server we have not deployed yet must all come out as "no URL", never a throw
 * on the error path.
 */
function movedUrlFrom(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return undefined
  const url = (data as { meeting_url?: unknown }).meeting_url
  return typeof url === 'string' && url.length > 0 ? url : undefined
}

function classify(status: number, message?: string, data?: unknown): JoinError {
  const text = (message ?? '').toLowerCase()
  if (text.includes('ended')) return new JoinError('ended', message ?? 'This class has ended')
  if (text.includes('not started') || text.includes('early')) {
    return new JoinError('not_started', message ?? 'This class has not started yet')
  }
  if (text.includes('zoom')) {
    return new JoinError('switched', message ?? 'This class moved to Zoom', movedUrlFrom(data))
  }
  // 425 Too Early: a hidden observer (Ghost) reached the room before any teacher
  // opened it — the room (and its recording) is created by the first real
  // participant to connect, never by an observer. Matched on STATUS, not the
  // message: the existing 'not started' text mapping above is the join-window
  // check (a 422 "come back nearer the start time"), a different thing entirely.
  // The classifier falls through to here only because 425 is not one of those
  // codes, so the ordering is safe.
  if (status === 425) {
    return new JoinError('waiting_for_class', message ?? 'Waiting for the class to open')
  }
  if (status === 401 || status === 403 || status === 404 || status === 410 || status === 422) {
    return new JoinError('expired', message ?? 'This link is no longer valid')
  }
  // 503 means the server is deliberately shedding load (circuit open, ~30s
  // cool-off). Classifying it as a generic error invites immediate re-tapping,
  // which on a shared mobile NAT burns the per-IP throttle for every other
  // student behind the same address during the exact window they should wait out.
  if (status === 503 || status === 429) {
    return new JoinError('unavailable', message ?? 'The class is temporarily unavailable')
  }
  return new JoinError('unknown', message ?? 'Something went wrong')
}

export function exchangePrejoin(code: string): Promise<PrejoinData> {
  return post<PrejoinData>({ code, phase: 'prejoin' })
}

export function exchangeConnect(code: string): Promise<ConnectData> {
  return post<ConnectData>({ code, phase: 'connect' })
}

export function exchangeReconnect(reconnectToken: string): Promise<ConnectData> {
  return post<ConnectData>({ reconnect_token: reconnectToken, phase: 'connect' })
}
